import React from 'react';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import type { OpStatus } from '../hooks/useAIOperation';

interface JobStatusProps {
  status: OpStatus;
  progress: string;
  error: string | null;
  onRetry?: () => void;
}

export default function JobStatus({ status, progress, error, onRetry }: JobStatusProps) {
  if (status === 'idle') return null;

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-xl sage-animate-slide-up"
      style={{
        backgroundColor: status === 'failed'
          ? 'color-mix(in srgb, var(--sage-danger) 10%, var(--sage-surface))'
          : status === 'succeeded'
          ? 'color-mix(in srgb, var(--sage-primary) 10%, var(--sage-surface))'
          : 'var(--sage-surface)',
      }}
    >
      {(status === 'submitting' || status === 'processing') && (
        <Loader2 size={18} className="sage-animate-spin" style={{ color: 'var(--sage-primary)' }} />
      )}
      {status === 'succeeded' && (
        <CheckCircle size={18} style={{ color: 'var(--sage-primary)' }} />
      )}
      {status === 'failed' && (
        <XCircle size={18} style={{ color: 'var(--sage-danger)' }} />
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm" style={{ color: 'var(--sage-text)' }}>
          {status === 'submitting' && 'Submitting to AI provider...'}
          {status === 'processing' && (progress || 'Processing...')}
          {status === 'succeeded' && 'Complete'}
          {status === 'failed' && (error || 'Operation failed')}
        </p>
      </div>

      {status === 'failed' && onRetry && (
        <button
          onClick={onRetry}
          className="text-xs px-3 py-1.5 rounded-lg transition-colors"
          style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text-muted)' }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

/** Inline cost estimate badge */
export function CostBadge({ cost, tier }: { cost: number; tier: string }) {
  if (tier === 'free') {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--sage-primary) 15%, transparent)',
          color: 'var(--sage-primary)',
        }}
      >
        Free
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--sage-accent) 15%, transparent)',
        color: 'var(--sage-accent)',
      }}
    >
      ~${cost.toFixed(2)}
    </span>
  );
}
